import api from '@/api'
import { utils } from '@/utils'
import { isEmpty } from 'lodash'
import vuex from '@/store'
import dayjs from 'dayjs'
import _ from 'lodash'

/**
 * Fields the API nulls out when it broadcasts a privatized story (ND-2335). One
 * payload goes to every subscriber on the station channel, so it is masked for
 * all of them — including users who may see the story in full. Those clients
 * already hold the real values from their own authorized fetch, so a masked
 * broadcast must leave them alone rather than overwrite them with null.
 */
const MASKED_STORY_FIELDS = ['summary', 'location', 'story_tags', 'tags_array']

/**
 * Counts the story modal's toolbar renders straight off the story object. A
 * child-record change never prompts — it just moves these numbers.
 */
const BROADCAST_COUNT_FIELDS = [
  'assignment_count',
  'task_count',
  'comment_count',
  'attachment_count',
  'media_asset_count',
  'script_count',
  'share_request_count'
]

/**
 * Story fields worth telling an editor about, and what to call them. Only these
 * are diffed for the "this story changed while you had it open" prompt.
 *
 * summary is deliberately absent. It is stored as the story's oldest comment,
 * not as a column, so an edit to it arrives as a comment change (which
 * refreshes silently) — and the broadcast carries the column while the open
 * story carries the resolved value, which would report a change on every event.
 *
 * updated_at and last_activity_at are absent for the same reason in reverse:
 * they move on every write and would make every event look like a change.
 */
const BROADCAST_DIFF_FIELDS = {
  title: 'Title',
  story_date: 'Story date',
  end_date: 'End date',
  story_time: 'Story time',
  end_time: 'End time',
  story_type: 'Story type',
  pillar: 'Pillar',
  status: 'Status',
  story_approved: 'Approval',
  location: 'Location',
  logistics: 'Logistics',
  source: 'Source',
  live_type: 'Live type',
  live_source: 'Live source',
  project_folder: 'Project folder',
  story_tags: 'Tags',
  is_privatized: 'Privacy'
}

/**
 * Tags are a set, not a list. The story grid's list computed re-sorts
 * story_tags into the options-table order and writes it back onto the story
 * object — which loadStory puts in both state.list and currentStory, the same
 * reference — so the open story and a fresh broadcast routinely carry the same
 * tags in a different order. Comparing the raw strings reported a tag change on
 * every event.
 */
function tagsKey(value) {
  return String(value ?? '')
    .split(',')
    .map((tag) => tag.trim())
    .filter(Boolean)
    .sort()
    .join(',')
}

const BROADCAST_DIFF_COMPARATORS = {
  story_tags: tagsKey
}

/**
 * What changed between the story an editor has open and the one just broadcast.
 * A masked field is skipped: null there means "not told", not "cleared".
 */
function describeStoryChanges(current, incoming) {
  return Object.entries(BROADCAST_DIFF_FIELDS)
    .filter(([field]) => incoming[field] !== undefined && incoming[field] !== null)
    .map(([field, label]) => ({ field, label, from: current?.[field] ?? '', to: incoming[field] }))
    .filter(({ field, from, to }) => {
      const normalize = BROADCAST_DIFF_COMPARATORS[field] ?? ((value) => String(value ?? ''))

      return normalize(from) !== normalize(to)
    })
}

/**
 * Decide what a broadcast means for the story the modal has open.
 *
 * Counts always move — the toolbar reads them straight off the story and a
 * child-record change has nothing else to say. Beyond that:
 *
 *   comment      the summary and the update feed. Refresh, do not ask: the user
 *                is not editing them.
 *   task,        a count and a list the modal loads itself. Scripts are edited
 *   assignment,  collaboratively and the media asset modal saves incrementally,
 *   script,      so both are already current by other means.
 *   media_asset
 *   story        someone edited the same record. Only this one can collide with
 *                what the user is typing, so it is the only one that asks.
 */
function reconcileOpenStory(commit, state, event) {
  const counts = {}
  BROADCAST_COUNT_FIELDS.forEach((field) => {
    if (event.story[field] !== undefined) counts[field] = event.story[field]
  })
  if (Object.keys(counts).length) commit('PATCH_CURRENT_STORY', counts)

  if (event.source === 'comment') {
    commit('SET_OPEN_STORY_EVENT', { kind: 'comments', storyId: event.story_id })
    return
  }

  if (event.source !== 'story') return

  // A write that touched nothing the user can see (a timestamp, a derived
  // column) is not worth interrupting anyone for.
  const changes = describeStoryChanges(state.currentStory, event.story)
  if (!changes.length) return

  commit('SET_OPEN_STORY_EVENT', { kind: 'updated', storyId: event.story_id, changes })
}

/**
 * Turn a broadcast story into fields safe to merge onto a row we already hold.
 */
function broadcastFields(story) {
  const fields = { ...story, story_date_label: isEmpty(story.story_date) ? 'TBD' : story.story_date }

  if (story.is_privatized) {
    MASKED_STORY_FIELDS.forEach((field) => {
      if (fields[field] === null) delete fields[field]
    })
  }

  return fields
}

/**
 * Turn a broadcast story into a new row. The list fetch asks for
 * include=assignments,comments and a broadcast never carries children, so seed
 * them empty: the grid's renderers expect the shape, and the backstop poll
 * fills them in. A story someone else has just created has none yet anyway.
 */
function broadcastRow(story) {
  return { assignments: [], comments: [], tasks: [], ...broadcastFields(story) }
}

const stories = {
  state: {
    list: [],
    monitoredStories: [],
    rundownItems: [],
    archives: [],
    archiveOptions: {},
    myassignments: [],
    mytasks: [],
    mystories: [],
    mypitches: [],
    plannerHeaderStories: [],
    currentAssignment: {},
    currentTask: {},
    currentStory: {},
    isDirty: false,
    linkedStory: {},
    parentStory: {},
    currentModalView: 'story',
    calendarStories: [],
    deletedStories: [],
    lastUpdatedAt: 0,
    publishingDistributionIds: [],
    // Set when a broadcast lands on the story the modal currently has open, so
    // StoryModal can decide what the user should see. Cleared once handled.
    openStoryEvent: null,
    // Bumped by the navbar badge so an already-mounted Pending Scripts page
    // resets its filters to match the count the user just clicked (ND-2285).
    // A fresh mount starts at defaults and ignores it.
    scriptsFilterResetRequest: 0
  },
  mutations: {
    REQUEST_SCRIPTS_FILTER_RESET(state) {
      state.scriptsFilterResetRequest++
    },
    LOAD_STORIES(state, list) {
      list.sort((a, b) => b.id - a.id)
      state.list = list
      console.debug(`LOAD_STORIES`, list)
    },
    LOAD_STORY(state, story) {
      const index = state.list.findIndex((s) => s.id == story.id)
      if (index > -1) state.list.splice(index, 1, story)
      else state.list.push(story)
      state.list.sort((a, b) => b.id - a.id)
      console.debug(`LOAD_STORY`, story)
    },
    PATCH_STORY(state, { id, fields }) {
      const index = state.list.findIndex((s) => s.id == id)
      if (index > -1) Object.assign(state.list[index], fields)
      console.debug(`PATCH_STORY`, id, fields)
    },
    UNLOAD_STORY(state, storyId) {
      // look in the store for the story to unload
      const index = state.list.findIndex((s) => s.id == storyId)
      // if we found it, remove it from the store
      if (index > -1) state.list.splice(index, 1)
      console.debug(`UNLOAD_STORY`, storyId)
    },
    LOAD_RUNDOWN_ITEMS(state, items) {
      state.rundownItems = items
      console.debug(`LOAD_RUNDOWN_ITEMS`, items)
    },
    LOAD_ARCHIVES(state, list) {
      state.archives = list
      console.debug(`LOAD_ARCHIVES`, list)
    },
    LOAD_MY_TASKS(state, data) {
      state.mytasks = data
      console.debug(`LOAD_MY_TASKS`, data)
    },
    LOAD_MY_ASSIGNMENTS(state, data) {
      state.myassignments = data
      console.debug(`LOAD_MY_ASSIGNMENTS`)
    },
    LOAD_MY_STORIES(state, stories) {
      stories.sort((a, b) => {
        let aDate = a.story_date ? dayjs(a.story_date).valueOf() : Infinity
        let bDate = b.story_date ? dayjs(b.story_date).valueOf() : Infinity
        return aDate - bDate
      })
      state.mystories = stories
      console.debug(`LOAD_MY_STORIES`, stories)
    },
    LOAD_MY_PITCHES(state, stories) {
      stories.sort((a, b) => {
        let aDate = a.story_date ? dayjs(a.story_date).valueOf() : Infinity
        let bDate = b.story_date ? dayjs(b.story_date).valueOf() : Infinity
        return aDate - bDate
      })
      state.mypitches = stories
      console.debug(`LOAD_MY_PITCHES`)
    },
    LOAD_PLANNER_HEADER_STORIES(state, stories) {
      stories.sort((a, b) => {
        let aDate = a.story_date ? dayjs(a.story_date).valueOf() : Infinity
        let bDate = b.story_date ? dayjs(b.story_date).valueOf() : Infinity
        return aDate - bDate
      })
      state.plannerHeaderStories = stories
      console.debug(`LOAD_PLANNER_HEADER_STORIES`)
    },
    LOAD_MONITORED_STORIES(state, stories) {
      stories.sort((a, b) => (a.story_date < b.story_date ? -1 : a.story_date > b.story_date ? 1 : 0))
      state.monitoredStories = stories
      console.debug(`LOAD_MONITORED_STORIES`, stories)
    },
    SET_STORY(state, object) {
      state.currentStory = object
    },
    SET_STORY_DIRTY(state, isDirty) {
      state.isDirty = isDirty
    },
    SET_LINKED_STORY(state, object) {
      state.linkedStory = object
    },
    SET_PARENT_STORY(state, object) {
      state.parentStory = object
    },
    SET_ASSIGNMENT(state, object) {
      state.currentAssignment = object
      console.debug(`SET_ASSIGNMENT`, object)
    },
    SET_TASK(state, object) {
      state.currentTask = object
      console.debug(`SET_TASK`, object)
    },
    SET_MODAL_VIEW(state, view) {
      state.currentModalView = view
    },
    LOAD_CALENDAR_STORIES(state, list) {
      state.calendarStories = list
      console.debug(`LOAD_CALENDAR_STORIES`, list)
    },
    SET_CALENDAR_STORIES(state, list) {
      state.calendarStories = list
      console.debug(`SET_CALENDAR_STORIES`, list)
    },
    LOAD_DELETED_STORIES(state, list) {
      state.deletedStories = list
      console.debug(`LOAD_DELETED_STORIES`, list)
    },
    SET_LAST_UPDATED_AT(state, updated_at_ts) {
      state.lastUpdatedAt = updated_at_ts
    },
    SET_OPEN_STORY_EVENT(state, event) {
      state.openStoryEvent = event
    },
    PATCH_CURRENT_STORY(state, fields) {
      if (!state.currentStory?.id) return
      // In place on purpose: the modal renders through Vue reactivity, and
      // replacing currentStory mid-edit is exactly the clobber this avoids.
      Object.assign(state.currentStory, fields)
    },
    SET_PUBLISHING_DISTRIBUTION_IDS(state, { action, distribution_id }) {
      if (action === 'add') {
        state.publishingDistributionIds.push(distribution_id)
      } else if (action === 'remove') {
        state.publishingDistributionIds = state.publishingDistributionIds.filter((id) => id !== distribution_id)
      } else if (action === 'clear') {
        state.publishingDistributionIds = []
      }
    }
  },
  actions: {
    async loadStories({ commit, state, dispatch }, skipCache) {
      // get list of stories from API
      const _s = await api.newsdesk.getStories('scope=active', skipCache)
      const stories = _s?.data ?? []

      // get list of attahments from API
      // const _a = await api.newsdesk.getAttachments('both', skipCache)
      // const attachments = _a?.data ?? []

      // link attachments to stories and label null story_date
      const list = stories.map((story) => {
        // story.attachments = attachments.filter((a) => a.story_id == story.id)
        story.story_date_label = isEmpty(story.story_date) ? 'TBD' : story.story_date
        return story
      })
      commit('LOAD_STORIES', list)

      // We should update the current story object also.
      if (state?.currentStory?.id) {
        let match = list.find((story) => story.id == state.currentStory.id)
        if (match) {
          dispatch('loadStory', { id: match.id, skipCache: true })
        }
      }

      dispatch('getRundowns', true).catch((error) => {
        console.error('Could not get rundowns', error)
      })
      dispatch('loadSubscriptions', true).catch((error) => {
        console.error('Could not get subscriptions', error)
      })
      dispatch('loadMonitoredStories', { skipCache: true }).catch((error) => {
        console.error('Could not get monitored stories', error)
      })
      dispatch('loadPresentTasks', true).catch((error) => {
        console.error('Could not get tasks', error)
      })
    },

    async loadMyStories({ commit, state, dispatch }, skipCache) {
      const _s = await api.newsdesk.getMyStories(skipCache)
      const stories = _s?.data ?? []
      commit('LOAD_MY_STORIES', stories)
    },
    async loadMyPitches({ commit, state, dispatch }, skipCache) {
      const _s = await api.newsdesk.getMyPitches(skipCache)
      const stories = _s?.data ?? []
      commit('LOAD_MY_PITCHES', stories)
    },
    async loadPlannerHeaderStories({ commit, state, dispatch }, skipCache = false) {
      const _s = await api.newsdesk.getPlannerHeaderStories(skipCache)
      const stories = _s?.data ?? []
      commit('LOAD_PLANNER_HEADER_STORIES', stories)
    },
    /**
     * Apply a StoryChanged broadcast to the list without fetching anything
     * (ND-2336). A fetch on receipt would turn one write into a read from every
     * open tab in the station, which is the cost this replaces.
     */
    applyStoryBroadcast({ commit, state }, event) {
      const storyId = event?.story_id
      if (!storyId) return

      // Keep the /changes watermark level with what push has already applied, so
      // the poll stops refetching the list on changes this tab received and only
      // fires on ones it missed. floor(), not ceil(): erring low costs a
      // redundant refetch, erring high would skip a real one.
      const appliedAt = Math.floor(Date.parse(event.timestamp) / 1000)
      if (Number.isFinite(appliedAt) && appliedAt > state.lastUpdatedAt) {
        commit('SET_LAST_UPDATED_AT', appliedAt)
      }

      const isOpenInModal = state.currentStory?.id == storyId

      if (event.action === 'deleted') {
        commit('UNLOAD_STORY', storyId)
        // Nothing else can be done for someone sitting in the modal: the story
        // is gone and every later fetch will agree. StoryModal tells them.
        if (isOpenInModal) commit('SET_OPEN_STORY_EVENT', { kind: 'deleted', storyId })
        return
      }

      if (!event.story) return

      if (isOpenInModal) reconcileOpenStory(commit, state, event)

      // Merge onto the row we hold, then commit the merged row rather than
      // patching in place. Two reasons, and both are load-bearing:
      //
      // The merge is because the broadcast carries the list row only — writing
      // event.story straight in would drop the assignments and comments the
      // list fetch loaded alongside it.
      //
      // The replace is because PATCH_STORY's in-place Object.assign never
      // reaches the grid. StoriesGrid watches its stories prop deeply and
      // bails on _.isEqual(new, old); the page's list computed rebuilds the
      // array but keeps the same element objects, so mutating a row in place
      // leaves the watcher comparing two arrays of identical objects and it
      // returns early. Splicing in a new object is what makes the row differ.
      const existing = state.list.find((s) => s.id == storyId)
      if (existing) {
        commit('LOAD_STORY', { ...existing, ...broadcastFields(event.story) })
        return
      }

      // Not held. Only a create belongs in the list; an update for a row we do
      // not have is a story outside the loaded scope, and inserting it would put
      // a row on screen the fetch deliberately left out.
      if (event.action === 'created') commit('LOAD_STORY', broadcastRow(event.story))
    },

    async getChanges({ commit, state, dispatch }) {
      let response = await api.newsdesk.getChanges()
      if (!response) return
      // find story change
      let storyChange = response.data?.find((c) => c.change_type == 'story') ?? {}

      if (state.lastUpdatedAt < storyChange.updated_at_ts) {
        commit('SET_LAST_UPDATED_AT', storyChange.updated_at_ts)
        await dispatch('loadStories', true)
      }
      return
    },

    async loadStory({ commit, state, dispatch }, { skipCache, id, setStory = true, deleted = false, station = vuex.getters.currentStation.station }) {
      const _s = await api.newsdesk.getStoryWithDetails(id, skipCache, deleted, station)
      const story = _s // ?.data?.find(s => s.id == id)
      if (!story) {
        throw new Error('Story not found')
      }
      if (story.station == vuex.getters.currentStation.station) {
        commit('LOAD_STORY', story)
      }
      if (setStory) dispatch('setStory', story)
      return story
    },

    async loadMonitoredStories({ commit, state, dispatch }, { params, skipCache } = {}) {
      // get list of stories from API
      const _s = await api.newsdesk.getMonitoredStories(params, skipCache)
      const stories = _s?.data ?? []

      commit('LOAD_MONITORED_STORIES', stories)
    },

    async getRundowns({ commit, state, dispatch }, skipCache) {
      try {
        const _i = await api.nrcs.getAllNewsdeskItems(skipCache)
        const items = _i?.data?.data ?? []
        commit('LOAD_RUNDOWN_ITEMS', items)
      } catch (error) {
        console.error('Could not get rundowns from NRCS', error)
      }
    },
    async getRundownItems({ commit, state, dispatch }, skipCache) {
      let assignments = state.list.reduce((assignments, story) => [...assignments, ...story.assignments], [])
      let remoteIds = assignments.filter((a) => a.remote_id).map((a) => a.remote_id)
      try {
        const _i = await api.nrcs.getStories(remoteIds, skipCache)
        const items = _i?.data?.data ?? []
        commit('LOAD_RUNDOWN_ITEMS', items)
      } catch (error) {
        console.error('Could not get rundown items from NRCS', error)
      }
    },
    async loadArchives({ commit, state, dispatch }, options) {
      if (options.keywords?.length < 3 && options.start == null) {
        return
      }

      const { skipCache, ...archiveOptions } = options
      state.archiveOptions = archiveOptions

      const response = await api.newsdesk.searchStories(options.keywords, options.start, options.end, 2500, options.skipCache)
      let list = response?.data ?? []
      list = _.uniqBy(list, 'id')
      if (response?.data?.errors?.length > 0) {
        list = []
        console.error(response?.data?.errors)
      }
      commit('LOAD_ARCHIVES', list)

      // We should update the current story object also.
      if (state?.currentStory?.id) {
        let match = list.find((story) => story.id == state.currentStory.id)

        if (match) {
          dispatch('setStory', match)
        }
      }
    },
    async setStory({ commit, state }, story) {
      if (state.currentStory?.id != story?.id) {
        commit('SET_LINKED_STORY', {})
      }

      commit('SET_STORY', story)
    },
    clearOpenStoryEvent({ commit }) {
      commit('SET_OPEN_STORY_EVENT', null)
    },
    async setStoryDirty({ commit, state }, isDirty) {
      commit('SET_STORY_DIRTY', isDirty)
    },
    async setLinkedStory({ commit }, story) {
      commit('SET_LINKED_STORY', story ?? {})
    },
    async setParentStory({ commit }, story) {
      commit('SET_PARENT_STORY', story)
    },
    async setAssignment({ commit }, assignment) {
      commit('SET_ASSIGNMENT', assignment)
    },
    async setTask({ commit }, task) {
      commit('SET_TASK', task)
    },
    async setModalView({ commit, state }, view) {
      commit('SET_MODAL_VIEW', view)
    },
    async loadMyTasks({ commit }, skipCache) {
      const response = await api.newsdesk.getMyTasks(skipCache)
      commit('LOAD_MY_TASKS', response?.data ?? [])
    },
    async loadMyAssignments({ commit }, skipCache) {
      const response = await api.newsdesk.getMyAssignments(skipCache)
      commit('LOAD_MY_ASSIGNMENTS', response?.data ?? [])
    },
    async loadCalendarStories({ commit }, options) {
      // commit('LOAD_CALENDAR_STORIES', [])
      const showDate = options?.showDate ?? new Date()
      const startDate = options?.startDate ? dayjs.tz(options.startDate.toISOString(), vuex.getters.currentTimezone).format('YYYY-MM-DD') : null
      const endDate = options?.endDate ? dayjs.tz(options.endDate.toISOString(), vuex.getters.currentTimezone).format('YYYY-MM-DD') : null

      let stories = []
      let tasks = []

      options.types = options.types ?? {}

      if (options.types.approved || options.types.unapproved || options.types.bust || options.types['multi-day']) {
        try {
          // build filter object
          let filter = {}

          // handle approval types
          let approvalTypes = Object.keys(options.types ?? {}).filter((key) => options.types[key] && key !== 'task')
          if (approvalTypes.length) filter.story_approved = approvalTypes.map((s) => utils.toTitleCase(s)).join(',')

          // handle story tags
          let tags = options.tags
          if (tags.length) filter.story_tags = tags.join(',')

          let rParams = {
            filter,
            startDate,
            endDate,
            include: '',
            limit: options?.limit ?? 2500
          }
          const response = await api.newsdesk.fetchStories(rParams, options?.skipCache)
          let storiesData = response?.data

          stories = storiesData.map((story) => {
            story.startDate = story.story_date
            story.endDate = story.end_date ?? story.story_date
            story.class = story.story_type.toLowerCase() === 'multi-day' ? 'event' : story.story_approved.toLowerCase()
            return story
          })
        } catch (error) {
          console.error('Failed to get stories for loadCalendarStories', error)
          throw error
        }
      }
      if (options.types?.task) {
        try {
          const tasksResponse = await api.newsdesk.searchTasks(
            {
              start_date: startDate,
              endDate: endDate,
              limit: 2500
            },
            options?.skipCache ?? false
          )
          let tasksData = tasksResponse?.data ?? []
          tasksData = tasksData.filter((t) => t.s_story_approved !== 'Bust')

          tasks = await Promise.all(
            tasksData.map(async (task) => {
              // let taskStory = await (_.head(task.story) ?? api.newsdesk.getStoryWithDetails(task.story_id)) // temp fix for missing story data in task data
              task.startDate = task.start_date
              task.originalTitle = task.title
              task.title = `${task.title}`.toUpperCase() + ` - ${task.story?.title}`
              task.class = 'task'
              // task.story = JSON.parse(JSON.stringify(taskStory))
              return task
            })
          )
        } catch (error) {
          console.error('Failed to get tasks for loadCalendarStories', error)
        }
      }

      commit('LOAD_CALENDAR_STORIES', stories.concat(tasks) ?? [])
    },
    setCalendarStories({ commit, state }, list) {
      commit('SET_CALENDAR_STORIES', list)
    },
    async updateCalendarStory({ commit, state, dispatch }, story_id) {
      if (story_id === null || story_id === undefined) {
        return
      }
      const updatedStory = await api.newsdesk.getStory({ id: story_id })
      const linkedStories = await api.newsdesk.loadLinkedStories(updatedStory.master_id, true)
      let items = state.calendarStories

      _.forEach(linkedStories.data ?? [], (story) => {
        story = Object.assign({}, story, {
          startDate: story.story_date,
          endDate: story.end_date ?? story.story_date,
          class: story.story_type.toLowerCase() === 'multi-day' ? 'event' : story.story_approved?.toLowerCase()
        })

        let indexOfExistingStory = _.findIndex(items, { id: story.id })
        if (indexOfExistingStory !== -1) {
          items.splice(indexOfExistingStory, 1, story)
        } else {
          items.push(story)
        }

        _.forEach(story.tasks ?? [], (task) => {
          task = Object.assign({}, task, {
            startDate: task.start_date,
            originalTitle: task.title,
            title: `${task.title}`.toUpperCase() + ` - ${story.title}`,
            class: 'task',
            story: JSON.parse(JSON.stringify(story))
          })

          let indexOfExistingTask = _.findIndex(items, { id: task.id, class: 'task' })
          if (indexOfExistingTask !== -1) {
            items.splice(indexOfExistingTask, 1, task)
          } else {
            items.push(task)
          }

          if (state.currentTask?.id === task?.id) {
            dispatch('setTask', task)
          }
        })
      })

      commit('SET_CALENDAR_STORIES', items)
    },
    deleteCalendarStory({ commit, state }, story_id) {
      if (story_id === null) {
        return
      }
      let items = state.calendarStories
      items = _.reject(items, (item) => story_id === item.id)
      commit('SET_CALENDAR_STORIES', items)
    },
    deleteCalendarTask({ commit, state, dispatch }, task) {
      if (task === null) {
        return
      }
      let items = state.calendarStories
      items = _.reject(items, (item) => item.class === 'task' && task.id === item.id)
      commit('SET_CALENDAR_STORIES', items)

      dispatch('updateCalendarStory', task.story_id)
    },
    async updateArchiveStory({ commit, state, dispatch }, story_id) {
      if (story_id === null || story_id === undefined) {
        return
      }
      let story = await api.newsdesk.getStoryWithDetails(story_id)

      story = Object.assign({}, story, {
        story_date_label: story.story_date
      })

      let items = state.archives

      let indexOfExistingStory = _.findIndex(items, { id: story.id })
      if (indexOfExistingStory !== -1) {
        items.splice(indexOfExistingStory, 1, story)
        dispatch('setStory', story)
      } else {
        items.push(story)
      }
      commit('LOAD_ARCHIVES', items)
    },
    async loadDeletedStories({ commit, state, dispatch }) {
      commit('LOAD_DELETED_STORIES', [])

      const _s = await api.newsdesk.getStories('deleted=1', true)
      const stories = _s?.data ?? []
      const list = stories.map((story) => {
        story.story_date_label = isEmpty(story.story_date) ? 'TBD' : story.story_date
        return story
      })

      if (state?.currentStory?.id) {
        let match = list.find((story) => story.id == state.currentStory.id)
        if (match) {
          dispatch('setStory', match)
        }
      }
      commit('LOAD_DELETED_STORIES', list)
    },
    addPublishingDistributionId({ commit }, distribution_id) {
      commit('SET_PUBLISHING_DISTRIBUTION_IDS', { action: 'add', distribution_id })
    },
    removePublishingDistributionId({ commit }, distribution_id) {
      commit('SET_PUBLISHING_DISTRIBUTION_IDS', { action: 'remove', distribution_id })
    },
    clearPublishingDistributionIds({ commit }) {
      commit('SET_PUBLISHING_DISTRIBUTION_IDS', { action: 'clear', distribution_id: null })
    }
  },
  getters: {
    stories(state) {
      return state.list
    },
    monitoredStories(state) {
      return state.monitoredStories
    },
    rundownItems(state) {
      return state.rundownItems
    },
    archives(state) {
      return state.archives
    },
    archiveOptions(state) {
      return state.archiveOptions
    },
    mytasks(state) {
      return state.mytasks
    },
    myassignments(state) {
      return state.myassignments
    },
    mystories(state) {
      return state.mystories
    },
    mypitches(state) {
      return state.mypitches
    },
    plannerHeaderStories(state) {
      return state.plannerHeaderStories
    },
    currentStory(state) {
      return state.currentStory ?? {}
    },
    isDirty(state) {
      return state.isDirty ?? false
    },
    linkedStory(state) {
      return state.linkedStory ?? {}
    },
    parentStory(state) {
      return state.parentStory ?? {}
    },
    currentTask(state) {
      return state.currentTask
    },
    currentAssignment(state) {
      return state.currentAssignment
    },
    currentModalView(state) {
      return state.currentModalView ?? 'story'
    },
    calendarStories(state) {
      return state.calendarStories
    },
    deletedStories(state) {
      return state.deletedStories
    },
    openStoryEvent(state) {
      return state.openStoryEvent
    },
    lastUpdatedAt(state) {
      return state.lastUpdatedAt
    },
    publishingDistributionIds(state) {
      return state.publishingDistributionIds
    },
    scriptsFilterResetRequest(state) {
      return state.scriptsFilterResetRequest
    }
  }
}

export default stories
